import { DashboardMapPinColour, PillVariant } from "../../widget";
import { ColorConstants } from "../styles";

export class ProjectTypeConstants {
  /* Dashboard map pin/key colour per project type, matched to the type's corresponding
     carbon standard so type and standard read consistently (see StandardConstants) */
  public static readonly PROJECT_TYPE_MAP_COLOUR_MAP: { [key: string]: DashboardMapPinColour } = {
    "Forestry and Woodland Creation": "green", // Woodland Carbon Code
    "Hedgerow and Agroforestry": "goldenrod", // Hedgerow Carbon Code
    Rewilding: "mint-green",
    Biodiversity: "violet", // Biodiversity Net Gain
    Peatland: "coral", // Peatland Code
    "Soil Carbon": "coral",
    "Bio Energy": "coral",
    "Regenerative Agriculture": "coral",
    "Seagrass Restoration": "aqua",
    "Constructed Wetland": "sky-blue",
    "Saltmarsh Restoration": "sky-blue",
    "Natural Flood Management": "sky-blue", // Natural Flood Management
    "Nutrient Neutrality": "sky-blue", // Nutrient Neutrality
    "Enhanced Weathering": "navy",
    Biochar: "navy",
    "Livestock Feed": "navy",
  };

  public static readonly PROJECT_TYPE_VARIANT_MAP: { [key: string]: PillVariant } = {
    // Green projects
    "Forestry and Woodland Creation": "green",
    "Hedgerow and Agroforestry": "green",
    Rewilding: "green",
    Biodiversity: "green",
    "Landscape Restoration": "green",

    // Brown projects
    Peatland: "coral",
    "Soil Carbon": "coral",
    "Bio Energy": "coral",
    "Regenerative Agriculture": "coral",

    // Blue projects
    "Seagrass Restoration": "blue",
    "Constructed Wetland": "blue",
    "Saltmarsh Restoration": "blue",
    "Natural Flood Management": "blue",
    "Nutrient Neutrality": "blue",

    // Grey projects
    "Enhanced Weathering": "grey",
    Biochar: "grey",
    "Livestock Feed": "grey",
  };

  /**
   * Chart colour per project type: `[segment, gradient end]`.
   *
   * Every type has its own colour — no two segments ever render the same. Each stays in
   * its family's colour region (greens for habitat, warm for soil, blues for water, grey
   * for engineered), but a family spans two neighbouring hues rather than four shades of
   * one, because chroma falls away at both ends of a single ramp: four shades of one hue
   * cannot all be vibrant. Shades are held to chroma >= 25 and lightness 38-80 so none
   * reads muddy or washed out.
   */
  public static readonly PROJECT_TYPE_CHART_COLOR_MAP: { [key: string]: string[] } = {
    // Green projects — habitat
    "Forestry and Woodland Creation": [ColorConstants.GREEN_500, ColorConstants.GREEN_100],
    "Hedgerow and Agroforestry": [ColorConstants.MINT_GREEN_500, ColorConstants.MINT_GREEN_100],
    Rewilding: [ColorConstants.GREEN_400, ColorConstants.GREEN_100],
    Biodiversity: [ColorConstants.GREEN_600, ColorConstants.GREEN_100],
    "Landscape Restoration": [ColorConstants.MINT_GREEN_600, ColorConstants.MINT_GREEN_100],

    // Brown projects — soil and peat
    Peatland: [ColorConstants.CORAL_500, ColorConstants.CORAL_100],
    "Soil Carbon": [ColorConstants.CORAL_600, ColorConstants.CORAL_100],
    "Bio Energy": [ColorConstants.CORAL_300, ColorConstants.CORAL_100],
    "Regenerative Agriculture": [ColorConstants.CORAL_400, ColorConstants.CORAL_100],

    // Blue projects — water and coastal
    "Seagrass Restoration": [ColorConstants.AQUA_400, ColorConstants.AQUA_100],
    "Constructed Wetland": [ColorConstants.AQUA_600, ColorConstants.AQUA_100],
    "Saltmarsh Restoration": [ColorConstants.SKY_BLUE_400, ColorConstants.SKY_BLUE_100],
    "Natural Flood Management": [ColorConstants.SKY_BLUE_300, ColorConstants.SKY_BLUE_100],
    "Nutrient Neutrality": [ColorConstants.SKY_BLUE_500, ColorConstants.SKY_BLUE_100],

    // Grey projects — engineered removal, deliberately the neutral of the set
    "Enhanced Weathering": [ColorConstants.GREY_400, ColorConstants.GREY_100],
    Biochar: [ColorConstants.GREY_300, ColorConstants.GREY_100],
    "Livestock Feed": [ColorConstants.GREY_200, ColorConstants.GREY_100],
  };

  public static readonly PROJECT_TYPE_PILL_COLOR_MAP: { [key: string]: string[] } = {
    // Green projects
    "Forestry and Woodland Creation": [ColorConstants.GREEN_800, ColorConstants.GREEN_100],
    "Hedgerow and Agroforestry": [ColorConstants.GREEN_800, ColorConstants.GREEN_100],
    Rewilding: [ColorConstants.GREEN_800, ColorConstants.GREEN_100],
    Biodiversity: [ColorConstants.GREEN_800, ColorConstants.GREEN_100],

    // Brown projects
    Peatland: [ColorConstants.CORAL_800, ColorConstants.CORAL_100],
    "Soil Carbon": [ColorConstants.CORAL_800, ColorConstants.CORAL_100],
    "Bio Energy": [ColorConstants.CORAL_800, ColorConstants.CORAL_100],
    "Regenerative Agriculture": [ColorConstants.CORAL_800, ColorConstants.CORAL_100],

    // Blue projects
    "Seagrass Restoration": [ColorConstants.AQUA_800, ColorConstants.AQUA_100],
    "Constructed Wetland": [ColorConstants.AQUA_800, ColorConstants.AQUA_100],
    "Saltmarsh Restoration": [ColorConstants.AQUA_800, ColorConstants.AQUA_100],
    "Natural Flood Management": [ColorConstants.AQUA_800, ColorConstants.AQUA_100],
    "Nutrient Neutrality": [ColorConstants.AQUA_800, ColorConstants.AQUA_100],

    // Grey projects
    "Enhanced Weathering": [ColorConstants.GREY_500, ColorConstants.GREY_100],
    Biochar: [ColorConstants.GREY_500, ColorConstants.GREY_100],
    "Livestock Feed": [ColorConstants.GREY_500, ColorConstants.GREY_100],
  };
}
